/**
 * Correo de la empresa — una sola tarjeta para todos los proveedores.
 *
 * REEMPLAZA a dos tarjetas que hacían lo mismo por caminos distintos
 * (SendGridConfigCard y MailjetConfigCard) y que guardaban en lugares
 * distintos. Medido antes de retirarlas: CERO empresas las habían usado, así
 * que no se pierde configuración de ningún cliente.
 *
 * SE DIBUJA SOLA. Los proveedores y sus campos vienen del backend, así que
 * agregar uno nuevo no obliga a tocar este archivo.
 *
 * TRES COSAS QUE HACE DISTINTO
 *  - La credencial nunca llega al navegador: se muestra cuántos caracteres
 *    tiene y en qué termina. Ni siquiera enmascarada, porque enmascarada
 *    significa que viajó.
 *  - Dejar el campo vacío NO borra la llave guardada. Eso fue lo que dejó a
 *    Casas KREA sin IA durante horas.
 *  - Antes de guardar se le pregunta al proveedor si la credencial sirve.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HelpTip from '@/components/HelpTip';
import { Mail, Loader2, CheckCircle2, AlertTriangle, Save, Plug, Globe, Bell } from 'lucide-react';

const ETIQUETAS = {
  api_key: 'API Key',
  api_secret: 'Secret Key',
  remitente_email: 'Correo del remitente',
  remitente_nombre: 'Nombre del remitente',
};

const AYUDAS = {
  remitente_email:
    'La dirección desde la que salen tus correos. Tiene que ser de un dominio que ya hayas '
    + 'verificado con tu proveedor: si mandas desde un dominio sin verificar, el correo se va a '
    + 'spam o ni sale.',
  remitente_nombre:
    'El nombre que ve el cliente en su bandeja, antes de abrir. Usa el de tu negocio, no el de '
    + 'una persona: "Casas KREA" abre más que "ventas1".',
};

const esSecreto = (campo) => campo === 'api_key' || campo === 'api_secret';

function ProveedorCorreo() {
  const [proveedores, setProveedores] = useState([]);
  const [config, setConfig] = useState(null);
  const [elegido, setElegido] = useState('');
  const [valores, setValores] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      api.get('/correo/proveedores'),
      api.get('/correo/config').catch(() => ({ data: { configurado: false } })),
    ]).then(([p, c]) => {
      setProveedores(p.data?.proveedores || []);
      setConfig(c.data);
      if (c.data?.proveedor) {
        setElegido(c.data.proveedor);
        const previos = {};
        Object.entries(c.data.credenciales || {}).forEach(([k, v]) => {
          if (!esSecreto(k)) previos[k] = v;
        });
        setValores(previos);
      }
    }).catch(() => toast.error('No se pudo cargar la configuración de correo'))
      .finally(() => setCargando(false));
  };
  useEffect(cargar, []); // eslint-disable-line

  const definicion = proveedores.find(p => p.clave === elegido);
  const yaConfigurado = config?.configurado && config?.proveedor === elegido;

  const guardar = () => {
    if (!elegido) { toast.error('Elige un proveedor'); return; }
    setGuardando(true);
    api.put('/correo/config', { proveedor: elegido, credenciales: valores })
      .then(r => {
        toast.success(r.data?.mensaje || 'Guardado');
        setValores(v => ({ ...v, api_key: '', api_secret: '' }));
        cargar();
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo guardar'))
      .finally(() => setGuardando(false));
  };

  const probar = () => {
    setProbando(true);
    api.post('/correo/config/probar')
      .then(r => (r.data?.ok ? toast.success(r.data.mensaje) : toast.error(r.data?.mensaje || 'No sirve')))
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo probar'))
      .finally(() => { setProbando(false); cargar(); });
  };

  const estado = config?.configurado && config?.activo;

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="correo-config-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Mail className="w-4 h-4 text-sky-400" />Correo de tu empresa
          <HelpTip
            title="Para qué sirve"
            content={
              'Aquí conectas tu propia cuenta de correo para que los envíos salgan CON TU DOMINIO '
              + 'y se cobren a tu cuenta, no a la de la plataforma.\n\n'
              + 'Es lo que usan los correos de marketing, el aviso de nuevo lead y la confirmación '
              + 'de cita.\n\n'
              + 'Si no lo configuras, esos envíos no salen. Preferimos eso a mandarlos con un '
              + 'dominio que no es tuyo: cuando alguien marca como spam un correo, la reputación '
              + 'que se quema es la del dominio que firma.'
            }
          />
          {estado
            ? <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">conectado</Badge>
            : <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground">sin conectar</Badge>}
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Elige tu proveedor y pega tus llaves. Antes de guardar le preguntamos si sirven.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {cargando ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label className="text-xs">Proveedor</Label>
                <HelpTip
                  title="Cuál elijo"
                  content={
                    'Los tres envían igual de bien. Elige el que ya uses o el que te acomode:\n\n'
                    + 'SendGrid: el más común, buena documentación.\n'
                    + 'Brevo: suele salir más barato por volumen.\n'
                    + 'Mailjet: pide DOS llaves en vez de una.\n\n'
                    + 'Solo aparecen proveedores que envían de verdad. Si no ves el tuyo, dinos y '
                    + 'lo agregamos.'
                  }
                />
              </div>
              <Select value={elegido} onValueChange={(v) => { setElegido(v); setValores({}); }}>
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="correo-proveedor">
                  <SelectValue placeholder="Elige tu proveedor de correo" />
                </SelectTrigger>
                <SelectContent>
                  {proveedores.map(p => (
                    <SelectItem key={p.clave} value={p.clave}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {definicion?.ayuda_llave && (
                <p className="text-[11px] text-muted-foreground">{definicion.ayuda_llave}</p>
              )}
            </div>

            {definicion && (
              <div className="space-y-3">
                {definicion.campos.map(campo => {
                  const previo = (config?.credenciales || {})[campo];
                  const yaHay = esSecreto(campo) && previo?.configurada && yaConfigurado;
                  return (
                    <div key={campo} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs">{ETIQUETAS[campo] || campo}</Label>
                          {AYUDAS[campo] && <HelpTip title={ETIQUETAS[campo]} content={AYUDAS[campo]} />}
                        </div>
                        {yaHay && (
                          <span className="text-[10px] text-emerald-400 font-mono">
                            guardada · {previo.longitud} caracteres · termina en {previo.final || '—'}
                          </span>
                        )}
                      </div>
                      <Input
                        type={esSecreto(campo) ? 'password' : 'text'}
                        autoComplete="new-password"
                        value={valores[campo] || ''}
                        onChange={e => setValores({ ...valores, [campo]: e.target.value })}
                        placeholder={
                          yaHay
                            ? 'Dejar vacío = no se toca. Escribir = la reemplaza.'
                            : (esSecreto(campo) ? 'Pega aquí tu llave' : '')
                        }
                        className="bg-black/20 border-white/10 font-mono text-xs"
                        data-testid={`correo-campo-${campo}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {config?.configurado && (
              <div className="p-3 rounded-lg bg-black/20 border border-white/5 flex items-start gap-2">
                {config.ultima_prueba_ok === false
                  ? <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  : <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />}
                <p className="text-[11px] text-muted-foreground">
                  {config.ultima_prueba_ok === false
                    ? 'La última prueba falló. Tus correos no están saliendo: revisa la llave.'
                    : 'Credencial verificada con el proveedor.'}
                  {config.verificado_en && ` (${String(config.verificado_en).slice(0, 10)})`}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline" size="sm" onClick={probar}
                disabled={probando || !config?.configurado}
                className="border-white/10 text-xs" data-testid="correo-probar"
              >
                {probando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plug className="w-3 h-3 mr-1" />}
                Probar la que ya está guardada
              </Button>
              <Button
                onClick={guardar} disabled={guardando || !elegido}
                className="bg-sky-500 text-white hover:bg-sky-600" data-testid="correo-guardar"
              >
                {guardando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar y verificar
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * El dominio de envío. Sin esto, el correo llega a spam o no llega:
 * Gmail y Outlook exigen dominio autenticado a quien manda volumen.
 *
 * La verdad la dice el PROVEEDOR, nunca el DNS. Se aprendió en CavernicolApp:
 * de las tres llaves que genera, a veces solo se publica una y el proveedor da
 * el dominio por bueno igual. Resolverlo por nuestra cuenta daría un
 * "pendiente" eterno y mandaría al cliente a recapturar registros correctos.
 */
function DominioEnvio({ licencia = null }) {
  const [estado, setEstado] = useState(null);
  const [dominio, setDominio] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState('');

  // licencia null = dominio de EMPRESA (idéntico a siempre). licencia
  // {entity_type, entity_id, nombre} = dominio por marca/desarrollo (endpoints
  // /correo/dominio/licencia). El resto del componente es el mismo (DRY).
  const esLic = !!licencia;
  const _ent = esLic ? { entity_type: licencia.entity_type, entity_id: licencia.entity_id } : null;

  const cargar = () => {
    (esLic
      ? api.get('/correo/dominio/licencia', { params: _ent })
      : api.get('/correo/dominio'))
      .then(r => { setEstado(r.data); if (r.data?.dominio) setDominio(r.data.dominio); })
      .catch(() => {});
  };
  useEffect(cargar, []); // eslint-disable-line

  const darDeAlta = () => {
    setOcupado(true);
    api.post(esLic ? '/correo/dominio/licencia' : '/correo/dominio',
             esLic ? { dominio, ..._ent } : { dominio })
      .then(r => {
        toast.success('Dominio dado de alta. Ahora captura los registros.');
        if (r.data?.cloudflare?.automatico) {
          toast.success(`Pusimos los ${r.data.cloudflare.puestos} registros por ti.`);
        }
        cargar();
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo dar de alta'))
      .finally(() => setOcupado(false));
  };

  const verificar = () => {
    setOcupado(true);
    api.post(esLic ? '/correo/dominio/licencia/verificar' : '/correo/dominio/verificar', esLic ? _ent : {})
      .then(r => (r.data?.estado === 'verificado' ? toast.success(r.data.mensaje) : toast.info(r.data.mensaje)))
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo verificar'))
      .finally(() => { setOcupado(false); cargar(); });
  };

  const copiar = (texto, id) => {
    navigator.clipboard?.writeText(texto);
    setCopiado(id);
    setTimeout(() => setCopiado(''), 1500);
  };

  const Fila = ({ etiqueta, valor, id }) => (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-white/55">{etiqueta}</span>
        <button onClick={() => copiar(valor, id)} className="text-[10px] text-sky-400 hover:text-sky-300 shrink-0">
          {copiado === id ? 'copiado' : 'copiar'}
        </button>
      </div>
      <code className="block text-[10px] text-emerald-200/90 bg-black/30 rounded px-2 py-1 break-all font-mono">{valor}</code>
    </div>
  );

  const listo = estado?.estado === 'verificado';

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5 mt-4" data-testid="correo-dominio-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Globe className="w-4 h-4 text-violet-400" />{esLic ? `Dominio de ${licencia.nombre}` : 'Tu dominio de envío'}
          <HelpTip
            title="Por qué es obligatorio"
            content={
              'Es lo que le demuestra a Gmail y a Outlook que los correos salen de verdad de tu '
              + 'empresa y no de alguien haciéndose pasar por ti.\n\n'
              + 'Sin esto, tus correos llegan a spam o no llegan. Desde 2024 los dos lo exigen a '
              + 'quien manda volumen.\n\n'
              + 'Son tres renglones que hay que capturar UNA vez donde vive tu dominio (el mismo '
              + 'lugar donde apuntaste tu página web). Si tu dominio lo administramos nosotros, '
              + 'los ponemos por ti.'
            }
          />
          {listo
            ? <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">verificado</Badge>
            : estado?.configurado
              ? <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">pendiente</Badge>
              : null}
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Puedes escribir tu correo, la dirección de tu página o solo el dominio: nosotros lo entendemos.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs">Dominio</Label>
            <Input
              value={dominio} onChange={e => setDominio(e.target.value)}
              placeholder="casaskrea.com" className="bg-black/20 border-white/10"
              data-testid="correo-dominio-input"
            />
          </div>
          <Button onClick={darDeAlta} disabled={ocupado || !dominio.trim()}
            className="bg-violet-500 text-white hover:bg-violet-600" data-testid="correo-dominio-alta">
            {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dar de alta'}
          </Button>
        </div>

        {estado?.configurado && (estado.registros || []).length > 0 && (
          <div className="space-y-2">
            {estado.cloudflare?.automatico ? (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-[11px] text-emerald-200/90">
                  Tu dominio lo administramos nosotros, así que pusimos los{' '}
                  {estado.cloudflare.puestos} registros por ti. Solo dale a comprobar.
                </p>
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Captura estos {estado.registros.length} renglones donde vive tu dominio.
                Cópialos tal cual, sin espacios de más. Pueden tardar hasta 48 horas en surtir efecto.
              </p>
            )}
            <div className="p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
              <p className="text-[10px] text-amber-200/80">
                <strong>Ojo con el nombre:</strong> algunos proveedores ya escriben tu dominio por su
                cuenta —lo verás en gris junto al campo— y otros esperan que lo pongas tú. Por eso te
                damos las dos versiones del nombre: usa la que corresponda a tu proveedor. Si te equivocas,
                tu dominio queda escrito dos veces y el registro no sirve.
              </p>
            </div>
            {estado.registros.map((reg, i) => (
              <div key={i} className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-2">
                <span className="block text-[10px] uppercase tracking-wider text-white/40">Renglón {i + 1} — tipo {reg.tipo}</span>
                {reg.nombre_corto && reg.nombre_completo ? (
                  <>
                    <Fila etiqueta="Nombre — si tu proveedor ya pone tu dominio solo (SiteGround, cPanel, Hostinger, Plesk)" valor={reg.nombre_corto} id={`c${i}`} />
                    <Fila etiqueta="Nombre — si te pide el nombre completo (Cloudflare, GoDaddy, Route 53)" valor={reg.nombre_completo} id={`n${i}`} />
                  </>
                ) : (
                  <Fila etiqueta="Nombre" valor={reg.nombre} id={`n${i}`} />
                )}
                <Fila etiqueta="Apunta a" valor={reg.valor} id={`v${i}`} />
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground">
              <strong>Si usas Cloudflare:</strong> deja la nubecita en GRIS, no naranja. Con la nube
              naranja, Cloudflare contesta por ti y la firma de tus correos deja de servir.
            </p>
            <Button variant="outline" size="sm" onClick={verificar} disabled={ocupado}
              className="border-white/10 text-xs w-full" data-testid="correo-dominio-verificar">
              {ocupado ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Ya los puse, comprueba si quedó
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Notificaciones por correo que el tenant puede prender/apagar.
 * Las de SISTEMA (recuperar contraseña) NO aparecen — siempre llegan.
 */
function NotificacionesTenant() {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = () => {
    api.get('/correo/notificaciones')
      .then((r) => setItems(r.data?.notificaciones || []))
      .catch(() => {})
      .finally(() => setCargando(false));
  };
  useEffect(cargar, []); // eslint-disable-line

  const ETIQ = {
    usuario_alta: 'Alta de usuario', usuario_baja: 'Baja de usuario',
    producto_baja: 'Baja de producto', licencia_baja: 'Cambio de licencia',
    lead_asignado: 'Lead asignado', lead_reasignado: 'Lead reasignado', lead_nuevo: 'Lead nuevo',
    lead_asignado_masivo: 'Lead asignado (masivo)', lead_reasignado_masivo: 'Lead reasignado (masivo)',
    oportunidad_ganada: 'Oportunidad ganada', oportunidad_perdida: 'Oportunidad perdida',
  };

  const toggle = (it) => {
    const nuevo = !it.activo;
    setItems((prev) => prev.map((x) => (x.tipo === it.tipo ? { ...x, activo: nuevo } : x)));
    api.put(`/correo/notificaciones/${it.tipo}`, { activo: nuevo })
      .then(() => toast.success(nuevo ? 'Aviso activado' : 'Aviso apagado'))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'No se pudo cambiar'); cargar(); });
  };

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5 mt-4" data-testid="correo-notificaciones-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-400" />Notificaciones por correo
          <HelpTip
            title="Qué es"
            content={'Prende o apaga los avisos por correo de tu operación (nuevo lead, oportunidad ganada, baja de usuario, etc.).\n\nLos de seguridad, como recuperar contraseña, siempre llegan y no aparecen aquí.'}
          />
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Elige qué avisos quieres recibir. Puedes apagar los que no te sirvan.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {cargando ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground">No hay avisos configurables por ahora.</p>
        ) : (
          items.map((it) => (
            <div key={it.tipo} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <span className="text-sm text-white">{ETIQ[it.tipo] || it.tipo}</span>
              <Button
                size="sm" variant={it.activo ? 'default' : 'outline'} onClick={() => toggle(it)}
                className={it.activo ? 'bg-emerald-600 hover:bg-emerald-700 text-xs' : 'border-white/10 text-muted-foreground text-xs'}
                data-testid={`correo-notif-${it.tipo}`}
              >
                {it.activo ? 'Activa' : 'Apagada'}
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Dominios por LICENCIA (marca / desarrollo). Fase 4 multidominio — capa OPCIONAL.
 * Por defecto todo sale desde el dominio de EMPRESA (arriba). Aquí cada marca/
 * desarrollo puede tener SU propio dominio; una campaña de esa licencia sale firmada
 * con él (si está verificado; si no, cae al de empresa — nunca se cae un envío).
 * Reusa DominioEnvio con el prop `licencia` (DRY).
 */
function DominiosPorLicencia() {
  const [licencias, setLicencias] = useState([]);
  const [sel, setSel] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/brands').catch(() => ({ data: [] })),
      api.get('/developments').catch(() => ({ data: [] })),
    ]).then(([b, d]) => {
      const lista = (x) => (Array.isArray(x) ? x : (x?.brands || x?.developments || x?.items || []));
      const bs = lista(b.data).map(x => ({ entity_type: 'brand', entity_id: x.id, nombre: x.name || x.nombre || 'Marca' }));
      const ds = lista(d.data).map(x => ({ entity_type: 'development', entity_id: x.id, nombre: x.name || x.nombre || 'Desarrollo' }));
      setLicencias([...bs, ...ds].filter(l => l.entity_id));
    }).catch(() => {});
  }, []);

  const licSel = licencias.find(l => `${l.entity_type}:${l.entity_id}` === sel) || null;

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5 mt-4" data-testid="correo-dominio-licencia-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Globe className="w-4 h-4 text-fuchsia-400" />Dominios por marca / desarrollo
          <HelpTip
            title="Para qué sirve"
            content={
              'Opcional. Por defecto todos tus correos salen desde el dominio de tu empresa (arriba).\n\n'
              + 'Si una marca o desarrollo tiene su propio dominio, aquí le das de alta el suyo: las '
              + 'campañas de esa marca/desarrollo saldrán firmadas con ESE dominio.\n\n'
              + 'Si el dominio de la licencia no está verificado, el correo igual sale — desde el '
              + 'dominio de tu empresa. Nunca se cae un envío por esto.'
            }
          />
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Elige una marca o desarrollo y dale su propio dominio de envío.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {licencias.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Todavía no tienes marcas ni desarrollos. El dominio de tu empresa (arriba) se usa para todo.
          </p>
        ) : (
          <>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="bg-black/20 border-white/10" data-testid="correo-licencia-selector">
                <SelectValue placeholder="Elige una marca o desarrollo" />
              </SelectTrigger>
              <SelectContent>
                {licencias.map(l => (
                  <SelectItem key={`${l.entity_type}:${l.entity_id}`} value={`${l.entity_type}:${l.entity_id}`}>
                    {l.nombre} {l.entity_type === 'brand' ? '(marca)' : '(desarrollo)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {licSel
              ? <DominioEnvio key={sel} licencia={licSel} />
              : <p className="text-[11px] text-muted-foreground">Elige una marca o desarrollo arriba para configurar su dominio.</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}


export default function CorreoConfig() {
  return (
    <>
      <ProveedorCorreo />
      <DominioEnvio />
      <DominiosPorLicencia />
      <NotificacionesTenant />
    </>
  );
}
